import React, { useState } from 'react';
import { 
  Table, 
  Card, 
  Input, 
  Select, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Statistic,
  message,
  Modal,
  Badge,
  Dropdown
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  UserOutlined,
  CrownOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { UserSubscriptionDetail, adminApi } from '../lib/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const ActiveSubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const {
    data: subscriptionsData,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['active-subscriptions', pagination.current, pagination.pageSize, tierFilter, statusFilter],
    queryFn: async () => {
      const params: any = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
      };

      if (tierFilter) params.tier = tierFilter;
      if (statusFilter) params.status = statusFilter;

      const result = await adminApi.getActiveSubscriptions(params);
      
      setPagination(prev => ({
        ...prev,
        total: result.total
      }));

      return result;
    },
    retry: 1,
    staleTime: 30000, // 30 seconds
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: ({ userId, tier, period, auto_renew }: { 
      userId: string; 
      tier: string; 
      period?: string; 
      auto_renew: boolean 
    }) => adminApi.updateUserSubscription(userId, tier, period, auto_renew),
    onSuccess: () => {
      message.success('Subscription updated successfully');
      queryClient.invalidateQueries({ queryKey: ['active-subscriptions'] });
    },
    onError: (error: any) => {
      message.error('Failed to update subscription: ' + (error.message || 'Unknown error'));
    }
  });

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      'free': 'default',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0'
    };
    return colors[tier.toLowerCase()] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'active': 'success',
      'cancelled': 'warning',
      'expired': 'error',
      'trial': 'processing'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleUpgradeSubscription = (subscription: UserSubscriptionDetail) => {
    const tiers = ['free', 'bronze', 'silver', 'gold', 'firooze'];
    const currentTierIndex = tiers.indexOf(subscription.tier.toLowerCase());
    const nextTier = tiers[currentTierIndex + 1];
    
    if (nextTier) {
      confirm({
        title: `Upgrade ${subscription.username || subscription.user_id} to ${nextTier}?`,
        content: `This will immediately upgrade the user's subscription tier from ${subscription.tier} to ${nextTier}.`,
        onOk: () => {
          updateSubscriptionMutation.mutate({
            userId: subscription.user_id,
            tier: nextTier,
            period: subscription.period,
            auto_renew: subscription.auto_renew
          });
        }
      });
    } else {
      message.info('User is already on the highest tier');
    }
  };

  const handleCancelSubscription = (subscription: UserSubscriptionDetail) => {
    confirm({
      title: `Cancel subscription for ${subscription.username || subscription.user_id}?`,
      content: 'This will set auto-renewal to false. The subscription will remain active until the current period ends.',
      onOk: () => {
        updateSubscriptionMutation.mutate({
          userId: subscription.user_id,
          tier: subscription.tier,
          period: subscription.period,
          auto_renew: false
        });
      }
    });
  };

  const filteredSubscriptions = subscriptionsData?.items?.filter((sub: UserSubscriptionDetail) => {
    const searchableText = [
      sub.username,
      sub.email,
      sub.user_id
    ].filter(Boolean).join(' ').toLowerCase();
    
    return !searchTerm || searchableText.includes(searchTerm.toLowerCase());
  }) || [];

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (subscription: UserSubscriptionDetail) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 500 }}>
              {subscription.username || subscription.user_id}
            </div>
            {subscription.email && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {subscription.email}
              </div>
            )}
          </div>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (subscription: UserSubscriptionDetail) => (
        <Space direction="vertical" size="small">
          <Tag 
            color={getTierColor(subscription.tier)} 
            icon={subscription.tier !== 'free' ? <CrownOutlined /> : undefined}
          >
            {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
          </Tag>
          <Text style={{ fontSize: '12px' }}>
            {subscription.period === 'annual' ? 'Annual' : 'Monthly'}
          </Text>
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: UserSubscriptionDetail) => (
        <Space direction="vertical" size="small">
          <Tag color={getStatusColor(status)} icon={
            status === 'active' ? <CheckCircleOutlined /> :
            status === 'trial' ? <ExclamationCircleOutlined /> :
            undefined
          }>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
          {record.auto_renew && (
            <Badge status="processing" text="Auto-renew" />
          )}
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Billing',
      key: 'billing',
      render: (subscription: UserSubscriptionDetail) => (
        <Space direction="vertical" size="small">
          {subscription.amount && (
            <div style={{ fontWeight: 500 }}>
              {formatCurrency(subscription.amount, subscription.currency)}
            </div>
          )}
          {subscription.payment_method && (
            <Text style={{ fontSize: '12px', color: '#666' }}>
              via {subscription.payment_method}
            </Text>
          )}
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (date: string) => (
        <div style={{ fontSize: '12px' }}>
          {formatDate(date)}
        </div>
      ),
      width: 100,
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string, record: UserSubscriptionDetail) => {
        if (!date) return <Text type="secondary">-</Text>;
        
        const expiryDate = new Date(date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        return (
          <div>
            <div style={{ fontSize: '12px' }}>
              {formatDate(date)}
            </div>
            {daysUntilExpiry <= 7 && daysUntilExpiry > 0 && (
              <Tag color="warning" style={{ fontSize: '11px' }}>
                {daysUntilExpiry} days left
              </Tag>
            )}
            {daysUntilExpiry <= 0 && (
              <Tag color="error" style={{ fontSize: '11px' }}>
                Expired
              </Tag>
            )}
          </div>
        );
      },
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (subscription: UserSubscriptionDetail) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View User Details',
            onClick: () => navigate(`/users/${subscription.user_id}`)
          },
          {
            key: 'upgrade',
            icon: <CrownOutlined />,
            label: 'Upgrade Plan',
            onClick: () => handleUpgradeSubscription(subscription),
            disabled: subscription.tier.toLowerCase() === 'firooze'
          },
          {
            key: 'cancel',
            icon: <StopOutlined />,
            label: 'Cancel Auto-Renewal',
            onClick: () => handleCancelSubscription(subscription),
            disabled: !subscription.auto_renew,
            danger: true
          }
        ];

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/users/${subscription.user_id}`)}
            >
              View
            </Button>
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
            >
              <Button
                size="small"
                icon={<MoreOutlined />}
                loading={updateSubscriptionMutation.isPending}
              />
            </Dropdown>
          </Space>
        );
      },
      width: 130,
    },
  ];

  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total,
    });
  };

  const activeCount = filteredSubscriptions.filter(sub => sub.status === 'active').length;
  const trialCount = filteredSubscriptions.filter(sub => sub.status === 'trial').length;
  const expiringCount = filteredSubscriptions.filter(sub => {
    if (!sub.expires_at) return false;
    const expiryDate = new Date(sub.expires_at);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  }).length;

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>Active Subscriptions</Title>
              <Text type="secondary">Manage and monitor user subscriptions</Text>
            </div>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Active"
              value={activeCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Trial"
              value={trialCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Expiring Soon"
              value={expiringCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Total"
              value={subscriptionsData?.total || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search subscriptions..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by tier"
              value={tierFilter}
              onChange={setTierFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="free">Free</Option>
              <Option value="bronze">Bronze</Option>
              <Option value="silver">Silver</Option>
              <Option value="gold">Gold</Option>
              <Option value="firooze">Firooze</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="active">Active</Option>
              <Option value="trial">Trial</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredSubscriptions}
          rowKey="user_id"
          loading={isLoading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} subscriptions`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default ActiveSubscriptions;